import { Page } from "../../../components/page-base";
import { AppDataGrid } from "../../../components/datagrid/AppDataGrid";
import { NameCell } from "../../../components/datagrid/NameCell";
import { ChipButton, TonalButton, OutlinedButton } from "../../../components/datagrid/ChipButton";
import { RankBadge, RateBar } from "../../../components/datagrid/RankBadge";
import { formatarNumero } from "../../../components/kpi-strip/formatar";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import type {
  DataGridColumn,
  DataGridRow,
  FilterChip,
} from "../../../components/datagrid/types";
import { Container } from "./styles";
import { isMobile } from "is-mobile";
import { useState, useMemo, useCallback, useEffect } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { toast } from "react-toastify";
import { CaseDetailsDialog } from "../components/case-details-dialog";
import { CasesService } from "../services";
import { useUserStore } from "../../../store/user";
import {
  FilterDrawer,
  FilterOption,
  SearchableMultiChipFilterConfig,
  SearchFilterConfig,
} from "../../../components/filter-drawer";
import { CategoriesService } from "../../../services/categories";
import { translateCategoryName } from "../../../utils/categoryTranslations";

export interface CaseProfile {
  id: number;
  publicId: string;
  ranking: number;
  name: string;
  email: string;
  location: string;
  rankedKeywords: number;
  rankedKeywordsLabel: string;
  totalKw: number;
  successRate: number;
}

const initialFilters = {
  localidade: "",
  categories: [] as FilterOption[],
};

export const Cases = () => {
  const { user } = useUserStore();

  const [profiles, setProfiles] = useState<CaseProfile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState<CaseProfile | null>(null);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const [debouncedLocalidade, setDebouncedLocalidade] = useState("");

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
  });

  type SortDirection = "asc" | "desc" | null;
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const handleSortRankedKeywords = useCallback(() => {
    setSortDirection((prev) =>
      prev === null ? "asc" : prev === "desc" ? "asc" : null
    );
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedLocalidade(filters.localidade), 400);
    return () => clearTimeout(timer);
  }, [filters.localidade]);

  useEffect(() => {
    const fetchCases = async () => {
      if (user) {
        setIsLoading(true);
        const categoryIds = filters.categories.map((c) => c.value);
        const res = await CasesService.getCases({
          token: user.token,
          page: pagination.page - 1,
          size: pagination.pageSize,
          ...(debouncedSearchTerm && { query: debouncedSearchTerm }),
          ...(sortDirection && { sort: `${sortDirection}` }),
          ...(debouncedLocalidade && { localidade: debouncedLocalidade }),
          ...(categoryIds.length > 0 && { categoryIds }),
        });

        if (res) {
          const mapped: CaseProfile[] = res.content.map((item: any) => ({
            id: item.position,
            publicId: item.publicId,
            ranking: item.position,
            name: item.name,
            email: "",
            location: `${item.district}, ${item.city}`,
            rankedKeywords: item.rankedKeywords,
            rankedKeywordsLabel: item.label,
            totalKw: item.totalKeywords,
            successRate: item.rankingRate,
          }));
          setProfiles(mapped);
          setPagination((p) => ({ ...p, totalCount: res.totalElements }));
        }
        setIsLoading(false);
      }
    };
    fetchCases();
  }, [user, pagination.page, pagination.pageSize, sortDirection, debouncedSearchTerm, debouncedLocalidade, filters.categories]);

  const navigate = useNavigate();

  const handlePageChange = useCallback((page: number) => {
    setPagination((p) => ({ ...p, page }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination((p) => ({ ...p, pageSize, page: 1 }));
  }, []);

  const top3 = useMemo(
    () =>
      pagination.page === 1 && !debouncedSearchTerm && !debouncedLocalidade && filters.categories.length === 0 && !sortDirection
        ? profiles.slice(0, 3)
        : [],
    [profiles, pagination.page, debouncedSearchTerm, debouncedLocalidade, filters.categories, sortDirection],
  );

  const handleNameClick = (profile: CaseProfile) => {
    setSelectedCase(profile);
    setIsDetailsModalOpen(true);
  };

  const handleCopyLink = (publicId: string) => {
    const link = `${window.location.origin}/dashboard/${publicId}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copiado para a área de transferência!");
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const searchableMultiChipFilters: SearchableMultiChipFilterConfig[] = useMemo(
    () => [
      {
        id: "categories",
        label: "Categorias",
        placeholder: "Buscar categoria...",
        onSearch: async (query: string) => {
          if (!user?.token) return [];
          const results = await CategoriesService.searchCategories(user.token, query);
          return results.map((c) => ({
            label: translateCategoryName(c.name),
            value: c.id,
          }));
        },
      },
    ],
    [user?.token]
  );

  const searchFilters: SearchFilterConfig[] = [
    {
      id: "localidade",
      label: "Localidade",
      placeholder: "Buscar por localidade",
      onSearch: () => {},
    },
  ];

  const filterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];

    if (filters.localidade) {
      chips.push({
        id: "localidade",
        label: `Local: ${filters.localidade}`,
        onDelete: () => handleFilterChange("localidade", ""),
      });
    }

    filters.categories.forEach((option) => {
      chips.push({
        id: `category-${option.value}`,
        label: option.label,
        onDelete: () =>
          handleFilterChange(
            "categories",
            filters.categories.filter((c) => c.value !== option.value)
          ),
      });
    });

    return chips;
  }, [filters]);

  const columns: DataGridColumn[] = [
    { id: "ranking", header: "#", minWidth: 60 },
    { id: "name", header: "Negócio", minWidth: 240 },
    { id: "location", header: "Localidade", minWidth: 150 },
    {
      id: "rankedKeywords",
      minWidth: 140,
      mobileLabel: "Ranqueadas",
      header: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          Ranqueadas
          <Tooltip
            title={
              sortDirection === null
                ? "Ordenar decrescente"
                : sortDirection === "desc"
                ? "Ordenar crescente"
                : "Remover ordenação"
            }
          >
            <IconButton size="small" onClick={handleSortRankedKeywords}>
              {sortDirection === "desc" ? (
                <ArrowDownwardIcon fontSize="inherit" />
              ) : sortDirection === "asc" ? (
                <ArrowUpwardIcon fontSize="inherit" />
              ) : (
                <UnfoldMoreIcon fontSize="inherit" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    { id: "totalKw", header: "Total", minWidth: 80 },
    { id: "successRate", header: "Taxa de sucesso", minWidth: 170 },
    { id: "actions", header: "", align: "right", minWidth: 190 },
  ];

  const rows: DataGridRow[] = useMemo(() => {
    return profiles.map((profile) => ({
      id: profile.id,
      render: {
        ranking: <RankBadge rank={profile.ranking} />,
        name: (
          <NameCell
            primaryText={profile.name}
            onClick={() => handleNameClick(profile)}
          />
        ),
        location: (
          <Typography variant="body2" sx={{ color: "#3C4043" }}>
            {profile.location}
          </Typography>
        ),
        rankedKeywords: (
          <span
            title={profile.rankedKeywordsLabel}
            style={{ fontFamily: "var(--font-sans)", fontWeight: 500, color: "#137333" }}
          >
            {profile.rankedKeywords}
          </span>
        ),
        totalKw: <span>{profile.totalKw}</span>,
        successRate: <RateBar rate={profile.successRate} />,
        actions: (
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
            <TonalButton type="button" onClick={() => navigate(`/dashboard/${profile.publicId}`)}>
              Abrir relatório
              <ChevronRightIcon sx={{ fontSize: 16 }} />
            </TonalButton>
            <Tooltip title="Copiar link do relatório" arrow>
              <IconButton size="small" onClick={() => handleCopyLink(profile.publicId)} aria-label="Copiar link do relatório">
                <ContentCopyOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    }));
  }, [profiles, navigate]);

  return (
    <Page.Root>
      <Container id="dashboard-container">
        <Page.HeaderAux />
        <Page.Header
          title="Casos de sucesso"
          subtitle="Perfis com excelente ranqueamento: 8 ou mais palavras-chave nas primeiras posições. Classificação pela taxa de sucesso medida pelo Radar."
        />
        {top3.length > 0 && (
          <Box sx={{ width: "calc(100% - 64px)", mt: 3 }}>
            <Box
              sx={{
                background: "#fff",
                border: "1px solid var(--line)",
                borderRadius: "16px",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "4px 16px",
                  p: { xs: "14px 16px", sm: "14px 20px" },
                  borderBottom: "1px solid var(--line)",
                  background: "#F8FAFC",
                }}
              >
                <Typography sx={{ fontFamily: "var(--font-sans)", fontSize: 14, fontWeight: 500 }}>
                  Os três melhores da carteira
                </Typography>
                <Typography sx={{ fontSize: 12, color: "var(--text-muted)" }}>
                  Classificação pela taxa de palavras ranqueadas
                </Typography>
              </Box>
              {top3.map((c) => (
                <Box
                  key={c.publicId}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "36px minmax(0, 1fr) minmax(0, 1fr)",
                      lg: "44px minmax(0, 1fr) 110px 120px 180px auto",
                    },
                    alignItems: "center",
                    gap: { xs: "12px 14px", lg: "16px" },
                    p: { xs: "14px 16px", lg: "14px 20px" },
                    // Abaixo de 1200 px: nome ao lado da posição, números lado a lado, taxa e ações embaixo.
                    "@media (max-width: 1199.95px)": {
                      "& > :nth-child(2)": { gridColumn: "2 / -1" },
                      "& > :nth-child(3)": { gridColumn: "2 / 3" },
                      "& > :nth-child(5)": { gridColumn: "2 / -1" },
                      "& > :nth-child(6)": { gridColumn: "1 / -1", justifyContent: "flex-start", flexWrap: "wrap" },
                    },
                    borderBottom: "1px solid #F1F3F4",
                    "&:last-child": { borderBottom: 0 },
                  }}
                >
                  <RankBadge rank={c.ranking} />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", minWidth: 0 }}>
                    <Typography
                      sx={{ fontFamily: "var(--font-sans)", fontSize: 16, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {c.name}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "var(--text-muted)" }}>{c.location}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <Typography sx={{ fontSize: 12, color: "var(--text-muted)" }}>Ranqueadas</Typography>
                    <Typography sx={{ fontFamily: "var(--font-sans)", fontSize: 20, fontWeight: 500, color: "#137333" }}>{c.rankedKeywords}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <Typography sx={{ fontSize: 12, color: "var(--text-muted)" }}>Total de palavras</Typography>
                    <Typography sx={{ fontFamily: "var(--font-sans)", fontSize: 20, fontWeight: 500 }}>{c.totalKw}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <Typography sx={{ fontSize: 12, color: "var(--text-muted)" }}>Taxa de sucesso</Typography>
                    <RateBar rate={c.successRate} />
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px" }}>
                    <TonalButton type="button" onClick={() => navigate(`/dashboard/${c.publicId}`)}>
                      <span className="material-icons">open_in_new</span>
                      Abrir relatório
                    </TonalButton>
                    <OutlinedButton type="button" style={{ height: 36, padding: "0 12px", fontSize: 13, borderRadius: 10 }} onClick={() => handleCopyLink(c.publicId)}>
                      <span className="material-icons" style={{ fontSize: 18 }}>content_copy</span>
                      Copiar link
                    </OutlinedButton>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        <Box
          sx={{ width: "calc(100% - 64px)", marginBottom: isMobile() ? "6vh" : "1px", mt: 3 }}
        >
          <AppDataGrid
            toolbarChips={
              <ChipButton type="button" $on aria-pressed>
                Todos · {formatarNumero(pagination.totalCount)}
              </ChipButton>
            }
            resultsLabel="Fonte: Radar GeoRanking"
            columns={columns}
            rows={rows}
            tableLabel="cases"
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            searchPlaceholder="Buscar por nome ou localidade..."
            onOpenFilters={() => setIsFilterDrawerOpen(true)}
            filterChips={filterChips}
            filterCount={filterChips.length}
            isLoading={isLoading}
            pagination={{
              ...pagination,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange,
            }}
          />
          <CaseDetailsDialog
            open={isDetailsModalOpen}
            onClose={() => setIsDetailsModalOpen(false)}
            caseData={selectedCase}
            onViewDetails={(publicId) => navigate(`/dashboard/${publicId}`)}
            onCopyLink={handleCopyLink}
          />
        </Box>
        <FilterDrawer
          open={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          searchableMultiChipFilters={searchableMultiChipFilters}
          searchFilters={searchFilters}
        />
      </Container>
    </Page.Root>
  );
};
